// --- Dependencies -------------------------------------------------------------------------------
import { useMemo, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyLang, TyOrderItem, TyOrderMenuCategory, TyOrderMenuPrice, TyOrderMenuProduct } from "@/types";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type OrderItemPickerDetails = {
    /** The order's lines as they stand. Edited here as a draft, and only handed back on "done". */
    items: TyOrderItem[];
    /** Which branch's catalogue to offer — its overrides are what the order will be priced against. */
    branch: string | null;
    onApply: (items: TyOrderItem[]) => void;
};

type Props = {
    onClose: () => void;
    details?: OrderItemPickerDetails;
};

type TyMenuAnswer = TyApiResponse<{ categories: TyOrderMenuCategory[]; products: TyOrderMenuProduct[] }>;

// --- Helpers ------------------------------------------------------------------------------------

/** What a tier actually costs — resolved by the API for this branch, never recomputed here. */
const amountOf = (price: TyOrderMenuPrice) => price.discountedAmount ?? price.amount;

/**
 * A line carries both languages, the way the API snapshots one. A line added here is only a draft —
 * the API re-reads every name from the catalogue on save — but it still has to read correctly in
 * whichever language the dashboard is in while it is one.
 */
const bothLangs = (ar: string, en: string): TyLang[] =>
    (
        [
            { lang: "ar", value: ar, default: true },
            { lang: "en", value: en, default: false },
        ] as TyLang[]
    ).filter((entry) => entry.value.trim());

// --- Logic --------------------------------------------------------------------------------------

/**
 * The menu, as the order's own branch sees it, with the order's lines laid over it.
 *
 * It reads `/orders/menu` rather than the menu editor's endpoint: permissions follow the URL's model
 * segment, so a member of staff who takes orders but may not touch the menu can still open this.
 *
 * A product is **one row with its sizes under its name**, not a row per size. A size is a choice
 * about one product, and listing "Shawarma large" beside "Shawarma regular" as two separate things to
 * find made a menu of forty products a list of ninety rows.
 *
 * Everything is a draft until "done": the counter can try a line, change their mind, and leave with
 * the order exactly as they found it.
 */
export default function OrderItemPickerPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const [items, setItems] = useState<TyOrderItem[]>(details?.items ?? []);
    const [category, setCategory] = useState("all");
    const [term, setTerm] = useState("");
    /** Which size each product is currently showing. Seeded below from whatever the order already holds. */
    const [sizes, setSizes] = useState<Record<string, string>>({});

    const { data, loading, error } = useFetch<TyMenuAnswer>("/orders/menu", { query: { branch: details?.branch ?? undefined } });

    const categories = data?.success ? data.data.categories : [];
    const products = useMemo(() => (data?.success ? data.data.products : []), [data]);

    const nameOf = (entry: { nameAr: string; nameEn: string }) => (lang === "ar" ? entry.nameAr || entry.nameEn : entry.nameEn || entry.nameAr);
    const labelOf = (price: TyOrderMenuPrice) =>
        price.showLabel === false ? "" : lang === "ar" ? price.labelAr || price.labelEn : price.labelEn || price.labelAr;

    const shown = useMemo(() => {
        const needle = term.trim().toLowerCase();
        return products.filter((product) => {
            if (category !== "all" && !product.categories.includes(category)) return false;
            if (!needle) return true;
            return `${product.nameAr} ${product.nameEn}`.toLowerCase().includes(needle);
        });
    }, [products, category, term, lang]);

    // --- Handlers --------------------------------------------------------------------------------

    /** The size on screen: the one just picked, else whichever of this product's tiers is on the order. */
    const tierOf = (product: TyOrderMenuProduct): TyOrderMenuPrice | undefined => {
        const picked = sizes[product._id];
        if (picked) return product.prices.find((price) => price._id === picked);
        const onOrder = items.find((item) => item.product === product._id);
        return product.prices.find((price) => price._id === onOrder?.priceId) ?? product.prices[0];
    };

    const countOf = (product: string, priceId: string) => items.find((item) => item.product === product && item.priceId === priceId)?.quantity ?? 0;

    const handleCount = (product: TyOrderMenuProduct, price: TyOrderMenuPrice, by: number) => {
        setItems((prev) => {
            const at = prev.findIndex((item) => item.product === product._id && item.priceId === price._id);
            if (at === -1) {
                if (by < 1) return prev;
                const line: TyOrderItem = {
                    product: product._id,
                    priceId: price._id,
                    name: bothLangs(product.nameAr, product.nameEn),
                    label: price.showLabel === false ? [] : bothLangs(price.labelAr, price.labelEn),
                    price: amountOf(price),
                    quantity: 1,
                };
                return [...prev, line];
            }

            const next = Math.min(constants.maxOrderItemQuantity, prev[at].quantity + by);
            // Stepping the last one off a line removes it rather than leaving a line of nothing.
            if (next < 1) return prev.filter((_item, index) => index !== at);
            return prev.map((item, index) => (index === at ? { ...item, quantity: next } : item));
        });
    };

    const handleApply = () => {
        details?.onApply(items);
        onClose();
    };

    // --- Render ----------------------------------------------------------------------------------

    if (!details) return null;

    const lines = items.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <div className="oip">
            <div className="oip-head">
                <p className="oip-title">{t.orders.pickItemTitle}</p>
                <span className="oip-count-badge">{t.orders.pickedCount.replace("{count}", String(lines))}</span>
            </div>

            <input
                className="oip-search"
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder={t.orders.pickItemSearch}
                autoComplete="off"
            />

            {/* The store's own sections, so a long menu is browsed the way the customer browses it. */}
            <div className="oip-tabs">
                <button type="button" className="oip-tab" data-on={category === "all"} onClick={() => setCategory("all")}>
                    {t.orders.filterAll}
                </button>
                {categories.map((entry) => (
                    <button key={entry._id} type="button" className="oip-tab" data-on={category === entry._id} onClick={() => setCategory(entry._id)}>
                        {nameOf(entry)}
                    </button>
                ))}
            </div>

            <div className="oip-list">
                {loading && <p className="oip-empty">{t.orders.pickItemLoading}</p>}
                {!loading && error && <p className="oip-error">{error}</p>}
                {!loading && !error && shown.length === 0 && <p className="oip-empty">{t.orders.pickItemEmpty}</p>}

                {shown.map((product) => {
                    const price = tierOf(product);
                    if (!price) return null;
                    const count = countOf(product._id, price._id);

                    return (
                        <div key={product._id} className="oip-row" data-on={count > 0}>
                            <div className="oip-row-main">
                                <p className="oip-row-name">{nameOf(product)}</p>

                                {/* One row per product; the sizes are a choice about it, not more products. */}
                                {product.prices.length > 1 && (
                                    <div className="oip-sizes">
                                        {product.prices.map((entry) => (
                                            <button
                                                key={entry._id}
                                                type="button"
                                                className="oip-size"
                                                data-on={entry._id === price._id}
                                                onClick={() => setSizes((prev) => ({ ...prev, [product._id]: entry._id }))}
                                            >
                                                <span>{labelOf(entry) || amountOf(entry)}</span>
                                                {countOf(product._id, entry._id) > 0 && (
                                                    <span className="oip-size-count">×{countOf(product._id, entry._id)}</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <span className="oip-row-price">{amountOf(price)}</span>

                            <div className="oip-steps">
                                <button
                                    type="button"
                                    className="oip-step"
                                    onClick={() => handleCount(product, price, -1)}
                                    disabled={count === 0}
                                    aria-label={t.orders.removeItemLabel}
                                >
                                    −
                                </button>
                                <span className="oip-step-count">{count}</span>
                                <button
                                    type="button"
                                    className="oip-step"
                                    onClick={() => handleCount(product, price, 1)}
                                    disabled={count >= constants.maxOrderItemQuantity}
                                    aria-label={t.orders.addItemLabel}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="oip-actions">
                {/* Leaves the order exactly as it was found — the whole list above is a draft. */}
                <button type="button" className="oip-cancel" onClick={onClose}>
                    {t.orders.editCancelLabel}
                </button>
                <button type="button" className="oip-done" onClick={handleApply} disabled={items.length === 0}>
                    {t.orders.pickItemDone}
                </button>
            </div>
        </div>
    );
}
